import React from 'react';
import noop from 'utils/noop';
import { createDropdownOption, isArray } from 'utils/helpers';
import { SemanticUI } from '@iterable/deprecated_react_components';
import {
  BulkActionsRowContainer,
  StyledCoreButton,
  StyledCoreButtonAlt,
  StyledCoredDropdown,
  SelectedItemContainer,
  ItemsSelectedLabel,
} from './BulkActionsRow.styled';

// type Props = {
//   /** List of actions to show.
//    *
//    * [{<br>
//    *  **label**: _string_ - Label of the aciton.<br><br>
//    *  **onClick(e, tableProps)**: _function_ - Callback functionwhen action is clicked.<br>
//    *    ```e```: _Object_: The click event.<br><br>
//    *    ```tableProps```: _Object_: The table props with including the updated selected rows.<br><br>
//    *  **options**: _Array_ - Passing options will render a dropdown. The onClick function will return the selected value in the tableProps paramter.<br><br>
//    *  **otherProps**: _Object_ - Additional props for the action button.<br><br>
//    * }]
//    */
//   actions: [
//     {
//       label: string,
//       onClick: () => {},
//       options: [{ value: string, text: string }],
//       otherProps: {},
//     },
//   ],
//   /** If actions should be rendered as buttons. */
//   actionsAsButtons: boolean,
//   /** Additional content. */
//   content: node,
//   /** Additional classes. */
//   className: string,
//   /** If buttons are iterable table style. */
//   isTableButtonStyle: boolean,
//   /** The name of a single item in the table. */
//   itemName: string,
//   /** The plural name of the items in the table. */
//   itemNamePlural: string,
//   /**
//    * Callback function when clearing selected rows.
//    *
//    * ```
//    * onClearSelection(e, rowProps) => {}
//    * ```
//    * @param { Object } e: The click event.
//    * @param { Object } rowProps: Props of selected rows including empty array of selected rows.
//    * @returns { void }
//    */
//   onClearSelection: (e: {}, rowProps: {}) => void,
//   /** The list of selected rows. */
//   selectedRows: array,
// };

export default class BulkActionsRow extends React.Component {
  static defaultProps = {
    actions: [],
    actionsAsButtons: false,
    onClearSelection: noop,
    selectedRows: [],
  };

  /**
   * getButtonComponent
   * Detemines if Button or Clickable gets renders.
   * @returns { React.Component } The actions button according to actionsAsButtons prop.
   */
  getButtonComponent = () => (this.props.actionsAsButtons ? StyledCoreButton : StyledCoreButtonAlt);

  /**
   * handleClick
   * Callback function when an action is clicked.
   * @param { Object } e: The click event.
   * @param { Object } updatedProps: The updated props containing the clicked index (name).
   * @returns { void }
   */
  handleClick = (e, { name }) => {
    const { actions } = this.props;
    const action = actions[name];
    if (action && action.onClick) {
      action.onClick(e, this.props);
    }
  };

  /**
   * handleChange
   * Callback function when a dropdown action is clicked.
   * @param { Object } e: The click event.
   * @param { Object } updatedProps: The updated props containing the clicked value.
   * @returns { void }
   */
  handleChange = (e, { name, value }) => {
    const { actions } = this.props;
    const action = actions[name];
    if (action && action.onClick && value !== undefined) {
      action.onClick(e, { ...this.props, value });
    }
  };

  /**
   * handleClearRows
   * Callback function when clear rows button is clicked.
   * @param { Object } e: The click event.
   * @returns { void }
   */
  handleClearRows = (e) => {
    const { onClearSelection } = this.props;
    onClearSelection(e, { ...this.props, selectedRows: [] });
  };

  /**
   * renderActionsDropdown
   * Creates an action dropdown.
   * @param { React.Component } trigger: The button trigger for the dropdown.
   * @param { Array } options: The dropdown options.
   * @param { Number } index: The index of the action in the bulk actions array.
   * @returns { React.Component } The action dropdown.
   */
  renderActionsDropdown = (trigger, options, index) => {
    const dropdownOptions = options.map(({ text, value, ...otherProps }, key) =>
      createDropdownOption(value, text, key, otherProps),
    );
    return (
      <StyledCoredDropdown
        key={`${index}-action`}
        name={index}
        onChange={this.handleChange}
        options={dropdownOptions}
        selectOnBlur={false}
        trigger={trigger}
        value=""
      />
    );
  };

  /**
   * renderActions
   * Creates the action buttons.
   * @returns { Array } The action buttons.
   */
  renderActions = () => {
    const { actions, isTableButtonStyle } = this.props;
    const ActionButton = this.getButtonComponent();
    return actions.map(({ label, options, otherProps }, index) => {
      const isDropdown = options && isArray(options);
      const button = (
        <ActionButton
          {...otherProps}
          key={`${index}-action`}
          name={index}
          onClick={isDropdown ? undefined : this.handleClick}
          table={isTableButtonStyle}
        >
          {label}
          {isDropdown && <SemanticUI.Icon name="dropdown" />}
        </ActionButton>
      );
      return isDropdown ? this.renderActionsDropdown(button, options, index) : button;
    });
  };

  renderItemCount = () => {
    const { isTableButtonStyle, itemName, itemNamePlural, selectedRows } = this.props;
    const ActionButton = this.getButtonComponent();
    const itemCount = selectedRows.length;
    const itemLabel = itemCount === 1 ? itemName : itemNamePlural;
    const selectedLabel = `${itemCount} ${itemLabel} selected`;
    return (
      <SelectedItemContainer>
        <ItemsSelectedLabel>{selectedLabel}</ItemsSelectedLabel>
        <ActionButton onClick={this.handleClearRows} table={isTableButtonStyle}>
          Clear
        </ActionButton>
      </SelectedItemContainer>
    );
  };

  render() {
    const { content, className, isTableButtonStyle } = this.props;
    const actions = this.renderActions();

    return (
      <BulkActionsRowContainer $isIterableStyle={isTableButtonStyle} className={className}>
        {actions}
        {content}
        {this.renderItemCount()}
      </BulkActionsRowContainer>
    );
  }
}
